import styled from "@emotion/styled";
import { FC, useState } from "react";

import { Search } from "@livechat/design-system-icons";
import {
  Avatar,
  Button,
  Checkbox,
  DesignToken,
  Icon,
  Input,
  Modal,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { SearchNoMatches } from "components/search-no-matches";
import { filterAgent } from "helpers/agent";
import { IAgent } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

interface IProps {
  agents: IAgent[];
  team: ITeam | null;
  value: string[];
  onConfirm(agentId: string[]): void;
  onCancel(): void;
}

function isOnlyTeam(teamId: string, agent: IAgent): boolean {
  return agent.teamIds.length === 1 && agent.teamIds[0] === teamId;
}

export const MembersModal: FC<IProps> = ({ agents, team, value: initialValue, onConfirm, onCancel }) => {
  const [filterQuery, setFilterQuery] = useState("");
  const [value, setValue] = useState(initialValue);

  const filteredAgents = filterQuery.trim() ? agents.filter(filterAgent(filterQuery)) : agents;
  const selectAllChecked = filteredAgents.every(agent => value.includes(agent.id));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterQuery(event.target.value);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSelectAll = () => {
    if (selectAllChecked) {
      setValue(value.filter(id => !filteredAgents.map(agent => agent.id).includes(id)));
    } else {
      setValue([...value, ...filteredAgents.map(agent => agent.id)]);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCheckboxClick = (checked: boolean, agentId: string) => {
    if (checked) {
      setValue(value.filter(id => id !== agentId));
    } else {
      setValue([...value, agentId]);
    }
  };

  return (
    <Modal
      heading="Choose agents"
      onClose={onCancel}
      footer={
        <Button
          kind="primary"
          size="compact"
          type="button"
          onClick={() => onConfirm(value)}
          disabled={value.length === 0}
        >
          Confirm
        </Button>
      }
    >
      <Container>
        <Block paddingH={withVar(SpacingToken.Spacing1)}>
          <Input
            placeholder="Search agents"
            icon={{
              source: <Icon source={Search} />,
              place: "left",
            }}
            id="filter-query"
            value={filterQuery}
            onChange={handleSearchChange}
          />

          <SelectAllWrapper>
            <Checkbox
              data-testid="select-all-agents-checkbox"
              checked={filteredAgents.every(agent => value.includes(agent.id))}
              onChange={handleSelectAll}
              disabled={filteredAgents.length === 0}
            >
              Select all
            </Checkbox>
          </SelectAllWrapper>
        </Block>

        <AgentItemsWrapper>
          {filteredAgents.map(agent => {
            const onlyTeam = team && isOnlyTeam(team.id, agent);
            const checked = value.includes(agent.id);
            return (
              <Flex
                data-testid="agent-row"
                key={agent.id}
                gap={withVar(SpacingToken.Spacing3)}
                alignItems="center"
                minHeight={withVar(SpacingToken.Spacing10)}
                marginBottom={withVar(SpacingToken.Spacing1)}
              >
                <Checkbox
                  checked={checked || !!onlyTeam}
                  onChange={() => handleCheckboxClick(checked, agent.id)}
                  disabled={!!onlyTeam}
                  data-agent-email={agent.email}
                />
                <Avatar
                  type={agent.avatar ? "image" : "text"}
                  src={agent.avatar ?? undefined}
                  text={agent.name ?? agent.email}
                  size="small"
                />
                <Flex column css={{ overflow: "hidden" }}>
                  <Text css={textEllipsisStyles}>
                    {agent.name || agent.email}{" "}
                    {onlyTeam && (
                      <span css={{ color: withVar(DesignToken.ContentBasicSecondary) }}>(their only team)</span>
                    )}
                  </Text>
                  <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>{agent.email}</Text>
                </Flex>
              </Flex>
            );
          })}

          {filteredAgents.length === 0 && filterQuery && <SearchNoMatches />}
        </AgentItemsWrapper>
      </Container>
    </Modal>
  );
};

const Container = styled.div`
  width: 424px;
  max-width: 100%;
`;

const AgentItemsWrapper = styled.div`
  padding: 0 ${withVar(SpacingToken.Spacing1)};
  display: flex;
  flex-direction: column;
  overflow: scroll;
  height: 330px;
  max-height: 100%;
`;

const SelectAllWrapper = styled.div`
  padding-top: ${withVar(SpacingToken.Spacing6)};
  padding-bottom: ${withVar(SpacingToken.Spacing2)};
  box-shadow: ${withVar(ShadowToken.DividerBottom)};
  margin-bottom: ${withVar(SpacingToken.Spacing3)};
`;
